import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Button, Card, EmptyState, Notice, SectionTitle, SelectField, TextField } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { useToast } from '../../../components/toast';
import { api, errorMessage } from '../../../lib/api';
import { fmtDateTime, fromLocalInput, toLocalInput } from '../../../lib/format';
import type { VehicleDto } from '../vehicles/types';

interface Blackout {
  id: string;
  reason: string;
  note: string | null;
  startsAt: string;
  endsAt: string;
  vehicleId: string | null;
}
const LABEL: Record<string, string> = { MAINTENANCE: 'Mantenimiento', VACATION: 'Vacaciones', UNAVAILABLE: 'No disponible', OTHER: 'Otro' };

export function BlackoutsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ['blackouts'], queryFn: () => api<Blackout[]>('/availability/blackouts') });
  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: () => api<VehicleDto[]>('/vehicles') });
  const [reason, setReason] = useState('UNAVAILABLE');
  const [start, setStart] = useState(toLocalInput(new Date(Date.now() + 86_400_000)));
  const [end, setEnd] = useState(toLocalInput(new Date(Date.now() + 2 * 86_400_000)));
  const [vehicleId, setVehicleId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [affected, setAffected] = useState<Array<{ code: string; scheduledAt: string }>>([]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api<Blackout & { affectedBookings: Array<{ code: string; scheduledAt: string }> }>('/availability/blackouts', {
        body: { reason, startsAt: fromLocalInput(start), endsAt: fromLocalInput(end), vehicleId: vehicleId || undefined, note: note || undefined },
      });
      setAffected(r.affectedBookings);
      await qc.invalidateQueries({ queryKey: ['blackouts'] });
      await qc.invalidateQueries({ queryKey: ['rides', 'calendar'] });
      toast('Horario bloqueado', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    await api(`/availability/blackouts/${id}`, { method: 'DELETE' }).catch((e) => toast(errorMessage(e), 'danger'));
    await qc.invalidateQueries({ queryKey: ['blackouts'] });
    await qc.invalidateQueries({ queryKey: ['rides', 'calendar'] });
  };

  return (
    <Page title="Bloquear horario" back="/driver/calendar">
      <div className="flex flex-col gap-5">
        <Card className="p-5 flex flex-col gap-3">
          <SelectField label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)}>
            {Object.entries(LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </SelectField>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Desde" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
            <TextField label="Hasta" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          {reason === 'MAINTENANCE' && (
            <SelectField label="Vehículo (opcional)" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} hint="Si eliges un vehículo, solo se bloquea ese vehículo">
              <option value="">Toda mi agenda</option>
              {vehicles.data?.map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} · {v.plate}</option>)}
            </SelectField>
          )}
          <TextField label="Nota" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button loading={busy} onClick={create}>Bloquear</Button>
        </Card>
        {affected.length > 0 && <Notice tone="warning">Reservas confirmadas dentro del bloqueo: {affected.map((a) => `${a.code} (${fmtDateTime(a.scheduledAt)})`).join(', ')}. No se cancelaron: reprográmalas o contacta a los clientes.</Notice>}
        <section>
          <SectionTitle title="Bloqueos activos" />
          {list.data?.length === 0 && <EmptyState title="Sin bloqueos" />}
          <div className="flex flex-col gap-2">
            {list.data?.map((b) => (
              <Card key={b.id} className="p-4 flex items-center gap-3">
                <div className="flex-1">
                  <p className="font-medium">{LABEL[b.reason]}{b.note ? ` · ${b.note}` : ''}</p>
                  <p className="text-sm text-[var(--text-muted)]">{fmtDateTime(b.startsAt)} → {fmtDateTime(b.endsAt)}</p>
                </div>
                <button className="tap flex items-center justify-center text-[var(--text-muted)] hover:text-brand-red" onClick={() => remove(b.id)} aria-label="Eliminar bloqueo"><Trash2 className="size-5" /></button>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </Page>
  );
}
